import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, Loader2, Lock, AlertCircle, CheckCircle2, LogOut } from 'lucide-react';

export default function BillingSetupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, fetchUser, logout, api } = useAuth();

  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const setupSessionId = params.get('setup_session_id');
  const canceled = params.get('canceled');

  const startCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/api/billing/setup-cc/start', { origin_url: origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start CC setup');
      setLoading(false);
    }
  };

  const confirmSetup = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment confirmation timed out. Please refresh this page.');
      setConfirming(false);
      return;
    }
    setConfirming(true);
    try {
      const res = await api.post(`/api/billing/setup-cc/confirm?session_id=${sessionId}`);
      if (res.data?.success && res.data?.cc_on_file) {
        setConfirmed(true);
        setConfirming(false);
        toast.success('Card saved! Redirecting to your workspace…');
        if (fetchUser) await fetchUser();
        setTimeout(() => {
          const wsId = res.data.workspace_id || user?.workspace_id;
          if (wsId) navigate(`/workspace/${wsId}`, { replace: true });
          else navigate('/', { replace: true });
        }, 1400);
      } else {
        // Not yet succeeded — poll again
        setPollCount(attempts + 1);
        setTimeout(() => confirmSetup(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to confirm setup');
      setConfirming(false);
    }
  }, [fetchUser, navigate, user?.workspace_id]);

  useEffect(() => {
    if (setupSessionId && !confirmed) {
      confirmSetup(setupSessionId);
    }
  }, [setupSessionId, confirmed, confirmSetup]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: '#0a0b0f' }}
      data-testid="billing-setup-page"
    >
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/20 mb-4">
            <CreditCard className="text-teal-400" size={26} />
          </div>
          <h1 className="text-3xl font-semibold text-white">Add Payment Method</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">
            Keep a card on file for usage-based top-ups and automatic subscription renewals after your trial.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6 space-y-4">
          {canceled && !setupSessionId && (
            <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 flex items-start gap-2 text-sm text-amber-300" data-testid="billing-setup-canceled-alert">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>Checkout was canceled. Adding a card is required to continue.</span>
            </div>
          )}

          {confirming && (
            <div className="rounded-lg border border-teal-400/30 bg-teal-400/10 p-4 flex items-center gap-3 text-sm text-teal-200" data-testid="billing-setup-confirming">
              <Loader2 size={18} className="animate-spin" />
              <span>Confirming your payment method… (attempt {pollCount + 1}/5)</span>
            </div>
          )}

          {confirmed && (
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 p-4 flex items-center gap-3 text-sm text-emerald-200" data-testid="billing-setup-success">
              <CheckCircle2 size={18} />
              <span>Card on file! Redirecting to your workspace…</span>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#0f1116] border border-white/5">
            <Lock className="text-slate-500 mt-0.5" size={16} />
            <div className="text-xs text-slate-400 leading-relaxed">
              Payments are processed securely by <span className="text-slate-200 font-medium">Stripe</span>. We never store your full card number.
              <br />
              <span className="text-slate-500">You won't be charged now.</span>
            </div>
          </div>

          {!setupSessionId && !confirmed && (
            <button
              onClick={startCheckout}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 transition-colors disabled:opacity-50"
              data-testid="billing-setup-start-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
              {loading ? 'Redirecting to Stripe…' : 'Add Card Securely'}
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1 pt-2"
            data-testid="billing-setup-logout-btn"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
